import React, { useMemo, useState } from "react";
import { ajaxPerso } from "../fnc";
import SheetCapture from "./ctn/SheetCapture";

const refConst = require("../constants.js").v;

const stores = [
  { id: refConst.id_store_eio, name: "EIO.com" },
  { id: refConst.id_store_hausfTools, name: "Haus of Tools" },
];

const markConflictingProducts = (items) => {
  const handlesByProduct = {};
  items.forEach((item) => {
    if (item.status !== "ready") return;
    const key = `${item.shop}-${item.product_id}`;
    handlesByProduct[key] = handlesByProduct[key] || new Set();
    handlesByProduct[key].add(item.version_handle);
  });
  return items.map((item) => {
    const key = `${item.shop}-${item.product_id}`;
    return item.status === "ready" &&
      handlesByProduct[key] &&
      handlesByProduct[key].size > 1
      ? { ...item, status: "conflict" }
      : item;
  });
};

export default function ShopifyBulkNewVersion(props) {
  const [dataSheet, setDataSheet] = useState(null);
  const [selectedStores, setSelectedStores] = useState(
    stores.map((store) => store.id),
  );
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);

  const readyItems = useMemo(
    () => preview.filter((item) => item.status === "ready"),
    [preview],
  );
  const missingItems = useMemo(
    () => preview.filter((item) => item.status === "not_found"),
    [preview],
  );
  const conflictItems = useMemo(
    () => preview.filter((item) => item.status === "conflict"),
    [preview],
  );

  const resetPreview = () => {
    setPreview([]);
    setResult(null);
    setProgress(null);
  };

  const buildRows = () => {
    const pairs = [];
    const duplicates = new Set();
    ((dataSheet && dataSheet.listObj) || []).forEach((row, index) => {
      const oldSku = String(row.old_sku || "").trim();
      const newSku = String(row.new_sku || "").trim();
      const key = `${oldSku}\u0000${newSku}`;
      if (oldSku && newSku && !duplicates.has(key)) {
        duplicates.add(key);
        pairs.push({
          source_row: index + 2,
          old_sku: oldSku,
          new_sku: newSku,
        });
      }
    });
    return pairs;
  };

  const makePreview = () => {
    const rows = buildRows();
    if (!rows.length)
      return props.info({
        error:
          "Use Select Data From Sheet and choose both the Old SKU and New SKU columns.",
      });
    if (!selectedStores.length)
      return props.info({ error: "Select at least one Shopify store." });
    setLoading(true);
    setResult(null);
    setProgress({ phase: "preview", total: rows.length * selectedStores.length, done: 0 });
    ajaxPerso(
      {
        api: "shopify",
        trigger: "bulk_new_version_preview",
        rows: JSON.stringify(rows),
        shops: JSON.stringify(selectedStores),
      },
      (response) => {
        setLoading(false);
        setProgress(null);
        if (response && response.success)
          setPreview(markConflictingProducts(response.items || []));
        else
          props.info({
            error:
              (response && response.error) ||
              "Could not find the Shopify products.",
          });
      },
    );
  };

  const removeItem = (index) =>
    setPreview((current) =>
      markConflictingProducts(
        current.filter((item, itemIndex) => itemIndex !== index),
      ),
    );

  const submit = () => {
    if (!readyItems.length)
      return props.info({
        error: "There are no matched products left to submit.",
      });
    const submitBatch = (items) =>
      new Promise((resolve) => {
        ajaxPerso(
          {
            api: "shopify",
            trigger: "bulk_new_version_submit",
            items: JSON.stringify(items),
          },
          resolve,
        );
      });

    const submitNow = async () => {
      setSubmitting(true);
      setProgress({ phase: "submit", total: readyItems.length, done: 0, updated: 0, failed: 0 });
      let done = 0;
      let updated = 0;
      let failed = 0;
      // The API pauses each Shopify write for rate safety. Small batches keep the
      // browser responsive and allow the progress bar to advance throughout a large push.
      for (let start = 0; start < readyItems.length; start += 20) {
        const batch = readyItems.slice(start, start + 20);
        const response = await submitBatch(batch);
        const batchDone = response && Array.isArray(response.results) ? response.results.length : batch.length;
        const batchUpdated = response && typeof response.updated === "number" ? response.updated : 0;
        const batchFailed = response && typeof response.failed === "number" ? response.failed : batchDone - batchUpdated;
        done += batchDone;
        updated += batchUpdated;
        failed += Math.max(0, batchFailed);
        setProgress({ phase: "submit", total: readyItems.length, done, updated, failed });
      }
      const finalResult = { success: failed === 0, updated, failed };
      setSubmitting(false);
      setResult(finalResult);
      if (finalResult.success)
        props.info({ success: `${updated} new-version metafields saved.` });
      else
        props.info({ error: `${updated} saved; ${failed} could not be saved. Review the result below.` });
    };
    props.modal({
      show: true,
      title: "Submit new-version metafields",
      html: () => (
        <p className="mb-0">
          Save {readyItems.length} reviewed product links across the selected
          Shopify stores?
        </p>
      ),
      ok: {
        title: "Submit",
        fnc: (popup, close) => {
          submitNow();
          close();
        },
      },
      return: { title: "Cancel" },
    });
  };

  return (
    <div className="container-fluid p-3">
      <h4 className="mb-1">Bulk New Item</h4>
      <p className="text-muted mb-4">
        Upload old/new SKU pairs, review every matched Shopify product, then
        save the replacement product&apos;s exact Shopify ID and handle. Both SKUs
        must resolve to a single product in each selected store before it can be submitted.
      </p>
      <div className="card mb-3">
        <div className="card-body">
          <div
            className="d-flex align-items-center flex-wrap mb-3"
            style={{ gap: "10px" }}
          >
            <SheetCapture
              {...props}
              dataSheet={dataSheet}
              returnAll={true}
              required={["old_sku", "new_sku"]}
              name="Select Data From Sheet"
              onChange={(value) => {
                setDataSheet(value);
                resetPreview();
              }}
            />
            <span className="text-muted small">
              Choose the Old SKU column first, then the New SKU column.
            </span>
            {dataSheet && (
              <span className="badge badge-secondary">
                {(dataSheet.listObj || []).length} rows selected
              </span>
            )}
            <button
              type="button"
              className="btn btn-outline-primary ml-auto"
              onClick={makePreview}
              disabled={loading}
            >
              {loading ? "Finding products..." : "Build preview"}
            </button>
          </div>
          <div>
            <span className="mr-3 font-weight-bold">Stores</span>
            {stores.map((store) => (
              <label key={store.id} className="mr-3 mb-0">
                <input
                  type="checkbox"
                  className="mr-1"
                  checked={selectedStores.indexOf(store.id) !== -1}
                  onChange={() => {
                    setSelectedStores((current) =>
                      current.indexOf(store.id) === -1
                        ? current.concat(store.id)
                        : current.filter((id) => id !== store.id),
                    );
                    resetPreview();
                  }}
                />
                {store.name}
              </label>
            ))}
          </div>
        </div>
      </div>
      {loading && (
        <div className="bulk-new-version__progress card mb-3 p-3">
          <div className="small mb-1">Finding Shopify products across the selected stores...</div>
          <div className="progress" aria-label="Finding Shopify products">
            <div className="progress-bar progress-bar-striped progress-bar-animated w-100" role="progressbar" />
          </div>
        </div>
      )}
      {preview.length > 0 && (
        <div className="card">
          {progress && progress.phase === "submit" && (
            <div className="bulk-new-version__progress px-3 pt-3">
              <div className="d-flex justify-content-between small mb-1">
                <span>{`Saving new-version metafields: ${progress.done} / ${progress.total}`}</span>
                <strong>{Math.round((progress.done / progress.total) * 100)}%</strong>
              </div>
              <div className="progress" aria-label="Bulk new item progress">
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                  aria-valuenow={progress.done}
                  aria-valuemin="0"
                  aria-valuemax={progress.total}
                />
              </div>
            </div>
          )}
          <div
            className="card-header d-flex justify-content-between align-items-center flex-wrap pl-3"
            style={{ gap: "10px" }}
          >
            <span className="pl-3">
              <strong>{readyItems.length}</strong> ready to submit
              {missingItems.length ? `, ${missingItems.length} not found` : ""}
              {conflictItems.length
                ? `, ${conflictItems.length} conflicting mappings`
                : ""}
            </span>
            <button
              type="button"
              className="btn btn-primary m-3"
              disabled={loading || submitting || !readyItems.length}
              onClick={submit}
            >
              {submitting
                ? "Submitting..."
                : `Submit ${readyItems.length} changes`}
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th className="pl-3">Store</th>
                  <th>Row</th>
                  <th>Old SKU</th>
                  <th>Product</th>
                  <th>New SKU</th>
                  <th>New Shopify product</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {preview.map((item, index) => (
                  <tr
                    key={`${item.shop}-${item.source_row}-${item.product_id || "missing"}-${index}`}
                    className={
                      item.status === "ready" ? "" : "bulk-new-version__issue"
                    }
                  >
                    <td className="pl-3">
                      {(stores.find((store) => store.id === item.shop) || {})
                        .name || item.shop}
                    </td>
                    <td>{item.source_row}</td>
                    <td>{item.old_sku}</td>
                    <td>
                      {item.status === "ready" || item.status === "conflict" ? (
                        <>
                          <div>{item.product_title}</div>
                          <small className="text-muted">
                            /{item.product_handle}
                          </small>
                        </>
                      ) : (
                        "No matching product"
                      )}
                    </td>
                    <td>{item.new_sku}</td>
                    <td>
                      {item.status === "ready" || item.status === "conflict" ? (
                        <>
                          <div>{item.version_title}</div>
                          <small className="text-muted">/{item.version_handle}</small>
                        </>
                      ) : item.status === "new_version_not_found" ? (
                        "New SKU is not in this Shopify store"
                      ) : item.status === "new_version_ambiguous" ? (
                        "More than one product has this New SKU"
                      ) : item.status === "same_product" ? (
                        "Same product as the old SKU"
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {item.status === "ready"
                        ? "Ready"
                        : item.status === "conflict"
                          ? "Conflict: remove one mapping"
                          : item.status === "new_version_not_found"
                            ? "New version not found"
                            : item.status === "new_version_ambiguous"
                            ? "New version is ambiguous"
                            : item.status === "same_product"
                              ? "Old and new SKU resolve to the same product"
                              : "Old product not found"}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={submitting}
                        onClick={() => removeItem(index)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {result && (
        <div
          className={`alert mt-3 ${result.success ? "alert-success" : "alert-warning"}`}
        >
          Updated: {result.updated || 0}. Failed: {result.failed || 0}.
        </div>
      )}
    </div>
  );
}
